import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';
import { Spinner } from '../components/UI';

function todayISO() {
  return new Date().toLocaleDateString('en-CA'); // yyyy-mm-dd in local tz
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('en-CA');
}

const RANGE_PRESETS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d',        label: 'Last 7 days' },
  { id: 'custom',    label: 'Custom' },
];

// stable-ish color per material
const PALETTE = ['#185FA5', '#0D9488', '#B8860B', '#7C3AED', '#C2410C', '#15803D', '#9F1239', '#0369A1', '#A16207', '#4338CA'];
function colorFor(i) { return PALETTE[i % PALETTE.length]; }

function fmtMoney(n) {
  if (n == null) return '—';
  return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
// Share of a total, to one decimal, printed the way the backend prints sharePct
// (40 rather than 40.0). Returns null when there is nothing to divide by —
// trips completed before the weighbridge rollout carry no net weight, so a
// range made only of those has zero tonnes and no share to show.
function pctOf(part, total) {
  if (!total || !part) return null;
  const p = Math.round((part / total) * 1000) / 10;
  return Number.isInteger(p) ? String(p) : p.toFixed(1);
}
function fmtTonnes(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 1 }) + ' t';
}

export default function Reports() {
  const [preset, setPreset] = useState('today');
  const [from, setFrom] = useState(todayISO());
  const [to, setTo]     = useState(todayISO());
  // custom-range pickers only commit into from/to (and trigger a fetch) on Apply/Enter,
  // not on every date pick — from/to stay the single source of truth for what's loaded
  const [pendingFrom, setPendingFrom] = useState(from);
  const [pendingTo, setPendingTo]     = useState(to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [downloadingMt, setDownloadingMt] = useState(false);
  const [downloadingVl, setDownloadingVl] = useState(false);

  function applyPreset(id) {
    setPreset(id);
    if (id === 'today') { setFrom(todayISO()); setTo(todayISO()); }
    else if (id === 'yesterday') { setFrom(daysAgoISO(1)); setTo(daysAgoISO(1)); }
    else if (id === '7d') { setFrom(daysAgoISO(6)); setTo(todayISO()); }
    else if (id === 'custom') { setPendingFrom(from); setPendingTo(to); }
  }

  function applyCustomRange() {
    setFrom(pendingFrom);
    setTo(pendingTo);
  }

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await gateApi.getMaterialTrips({ from, to })); }
    catch (e) { setErr(e.response?.data?.error || 'Could not load report.'); }
    finally { setLoading(false); }
  }, [from, to]);



  useEffect(() => { load(); }, [load]);

  async function handleDownload(kind) {
    const setDownloading = kind === 'material' ? setDownloadingMt : setDownloadingVl;
    const fn = kind === 'material' ? gateApi.downloadMaterialTripsPdf : gateApi.downloadVehicleLoadsPdf;
    setDownloading(true);
    try { await fn({ from, to }); }
    catch (e) { setErr(e.message || 'Could not generate PDF.'); }
    finally { setDownloading(false); }
  }

  const maxTrips = data?.materials?.reduce((m, x) => Math.max(m, x.trips), 0) || 1;

  const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' };
  const lbl  = { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' };

  return (
    <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Controls ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {RANGE_PRESETS.map(p => (
            <button key={p.id} onClick={() => applyPreset(p.id)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${preset === p.id ? '#185FA5' : 'var(--border)'}`,
              background: preset === p.id ? '#E6F1FB' : '#fff',
              color: preset === p.id ? '#0C447C' : 'var(--text-muted)',
            }}>{p.label}</button>
          ))}
        </div>
        <button onClick={() => handleDownload('material')} disabled={downloadingMt} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: downloadingMt ? 'default' : 'pointer',
          border: '1px solid #185FA5', background: '#185FA5', color: '#fff', opacity: downloadingMt ? 0.7 : 1,
        }}>
          {downloadingMt && <Spinner size={13} color="#fff" />}
          Download PDF
        </button>
      </div>

      {preset === 'custom' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={pendingFrom} max={pendingTo}
            onChange={e => setPendingFrom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyCustomRange(); }}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'var(--font-mono)' }} />
          <span style={{ color: 'var(--text-muted)' }}>to</span>
          <input type="date" value={pendingTo} min={pendingFrom} max={todayISO()}
            onChange={e => setPendingTo(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyCustomRange(); }}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'var(--font-mono)' }} />
          <button onClick={applyCustomRange} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Apply</button>
          {(pendingFrom !== from || pendingTo !== to) && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>Unapplied changes</span>
          )}
        </div>
      )}

      {err && <div style={{ ...card, borderColor: '#F7C1C1', background: '#FCEBEB', color: '#791F1F', fontSize: 13 }}>{err}</div>}

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 4px' }}>Loading…</div>
      ) : !data ? null : data.totalTrips === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '36px 20px' }}>
          No completed trips between {data.from} and {data.to}.
        </div>
      ) : (
        <>
          {/* ── Headline insights ──────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={card}>
              <div style={lbl}>Total trips</div>
              <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#0C447C' }}>{data.totalTrips}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.from} → {data.to}</div>
            </div>
            <div style={card}>
              <div style={lbl}>Most moved</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{data.insights?.topMaterial || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.insights?.topMaterialTrips || 0} trips</div>
            </div>
            <div style={card}>
              <div style={lbl}>Busiest day</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{data.insights?.busiestDay || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.insights?.busiestDayTrips || 0} trips</div>
            </div>
            <div style={card}>
              <div style={lbl}>Avg / day</div>
              <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#0C447C' }}>{data.insights?.avgTripsPerDay ?? '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.insights?.distinctMaterials || 0} materials</div>
            </div>
            <div style={card}>
              <div style={lbl}>Total tonnes moved</div>
              <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#0C447C' }}>{fmtTonnes(data.totalTonnes)}</div>
            </div>
            <div style={card}>
              <div style={lbl}>Amount received</div>
              <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#15803D' }}>{fmtMoney(data.totalRevenue)}</div>
            </div>
          </div>

          {/* ── Material breakdown (the core view) ─────────────────────────────── */}
          <div style={card}>
            <div style={{ ...lbl, marginBottom: 14 }}>Trips by material · vehicle split</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {data.materials.map((m, i) => (
                <div key={m.material}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{m.material}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-primary)' }}>
                      {m.trips} <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({m.sharePct}% of trips)</span>
                    </span>
                  </div>
                  {/* bar */}
                  <div style={{ height: 10, background: '#F1EFE8', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{ width: `${(m.trips / maxTrips) * 100}%`, height: '100%', background: colorFor(i), borderRadius: 5 }} />
                  </div>
                  {/* tonnage / revenue. The tonnes share sits beside the tonnes
                      and is labelled, because it routinely disagrees with the
                      trip share above it — a 12WHL and an 18WHL are both one
                      trip, so trips alone understate the material the big
                      vehicles carry. */}
                  <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {fmtTonnes(m.tonnes)}
                      {pctOf(m.tonnes, data.totalTonnes) && ` (${pctOf(m.tonnes, data.totalTonnes)}% of tonnes)`}
                    </span>
                    <span style={{ color: '#15803D', fontWeight: 600 }}>{fmtMoney(m.revenue)}</span>
                  </div>
                  {/* vehicle split */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 7 }}>
                    {m.vehicles.map(v => (
                      <span key={v.vehicleType} style={{
                        fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)',
                        background: '#F8F9FA', border: '1px solid var(--border)', borderRadius: 7, padding: '3px 9px',
                      }}>
                        {v.vehicleType}: <strong style={{ color: 'var(--text-primary)' }}>{v.trips}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Per-day trend ──────────────────────────────────────────────────── */}
          {data.days?.length > 1 && (
            <div style={card}>
              <div style={{ ...lbl, marginBottom: 14 }}>Trips per day</div>
              <DayBars days={data.days} />
            </div>
          )}

          {/* ── Overall vehicle mix ────────────────────────────────────────────── */}
          <div style={card}>
            <div style={{ ...lbl, marginBottom: 12 }}>Vehicle mix (all materials)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {data.vehicleMix.map(v => (
                <span key={v.vehicleType} style={{
                  fontSize: 13, fontFamily: 'var(--font-mono)',
                  background: '#F8F9FA', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px',
                }}>
                  {v.vehicleType}: <strong>{v.trips}</strong>
                </span>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', paddingBottom: 8 }}>
            Based on physical exit time. Will switch to challan date once challan is live.
          </div>
        </>
      )}

      {/* ── AI Load Accuracy Report (PDF-only, independent of the material-trips fetch above) ── */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={lbl}>AI Load Accuracy Report</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Per-trip AI-predicted vs final load, with manual overrides flagged.
          </div>
        </div>
        <button onClick={() => handleDownload('vehicle')} disabled={downloadingVl} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: downloadingVl ? 'default' : 'pointer',
          border: '1px solid #185FA5', background: '#fff', color: '#185FA5', opacity: downloadingVl ? 0.7 : 1,
        }}>
          {downloadingVl && <Spinner size={13} color="#185FA5" />}
          Download PDF
        </button>
      </div>
    </div>
  );
}

function DayBars({ days }) {
  const max = days.reduce((m, d) => Math.max(m, d.trips), 0) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto' }}>
      {days.map(d => (
        <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 34 }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{d.trips}</span>
          <div style={{ width: 22, height: `${(d.trips / max) * 100}%`, minHeight: 3, background: '#185FA5', borderRadius: '4px 4px 0 0' }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}